/**
 * Saqr Skills Product Page
 *
 * Product information page for Saqr Skills platform.
 * Supports English and Arabic with RTL layout.
 *
 * Note: dangerouslySetInnerHTML is used for translation strings containing
 * HTML tags (like <strong>). These strings come from our own JSON translation
 * files, not user input, so XSS risk is mitigated.
 */

import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageToggle } from '../components/LanguageToggle';
import { SEO } from '../components/SEO';
import { useLocalizedPath } from '../lib/language-context';
import { getBreadcrumbSchema } from '../lib/structured-data';
import './LandingPage.css';

export default function TermsPage() {
  const { t, i18n } = useTranslation(['terms', 'common', 'seo']);
  const localizedPath = useLocalizedPath();
  const isArabic = i18n.language === 'ar';
  const currentPath = isArabic ? '/ar/terms' : '/terms';

  // Structured data
  const structuredData = getBreadcrumbSchema([
    { name: isArabic ? 'الرئيسية' : 'Home', path: isArabic ? '/ar' : '' },
    { name: t('seo:terms.title'), path: currentPath },
  ]);

  // IntersectionObserver for scroll animations (same as LandingPage)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const voiceBestForItems = t('assessmentTypes.voice.bestForItems', { returnObjects: true }) as string[];
  const writtenBestForItems = t('assessmentTypes.written.bestForItems', { returnObjects: true }) as string[];
  const trainerSteps = t('trainerExperience.steps', { returnObjects: true }) as string[];
  const scoringFeatures = t('scoring.features', { returnObjects: true }) as string[];
  const scoringImmutableItems = t('scoring.immutableItems', { returnObjects: true }) as string[];
  const enterpriseFeatures = t('enterprise.features', { returnObjects: true }) as { title: string; desc: string }[];
  const whoForItems = t('whoFor.items', { returnObjects: true }) as string[];

  const learnerFeatures = [
    { icon: 'lucide:hand-wave', title: t('learnerExperience.features.welcome.title'), desc: t('learnerExperience.features.welcome.desc') },
    { icon: 'lucide:book-open', title: t('learnerExperience.features.briefing.title'), desc: t('learnerExperience.features.briefing.desc') },
    { icon: 'lucide:compass', title: t('learnerExperience.features.guided.title'), desc: t('learnerExperience.features.guided.desc') },
  ];

  const voiceItems = t('learnerExperience.voice.items', { returnObjects: true }) as string[];
  const writtenItems = t('learnerExperience.written.items', { returnObjects: true }) as string[];

  const scoringIconFeatures = [
    { icon: 'lucide:bot', label: scoringFeatures[0] },
    { icon: 'lucide:target', label: scoringFeatures[1] },
    { icon: 'lucide:file-search', label: scoringFeatures[2] },
    { icon: 'lucide:gauge', label: scoringFeatures[3] },
  ];

  const scoringImmutableIcons = [
    { icon: 'lucide:scale', label: scoringImmutableItems[0] },
    { icon: 'lucide:bar-chart-2', label: scoringImmutableItems[1] },
    { icon: 'lucide:shield-check', label: scoringImmutableItems[2] },
  ];

  const whoForIcons = [
    { icon: 'lucide:graduation-cap', text: whoForItems[0] },
    { icon: 'lucide:building-2', text: whoForItems[1] },
    { icon: 'lucide:file-check', text: whoForItems[2] },
    { icon: 'lucide:shield', text: whoForItems[3] },
  ];

  return (
    <div className="landing-page antialiased light">
      <SEO
        title={t('seo:terms.title')}
        description={t('seo:terms.description')}
        path={currentPath}
        structuredData={structuredData}
      />
      <div className="relative min-h-screen overflow-x-hidden pt-32 bg-white text-slate-900 selection:bg-sky-500/20 selection:text-sky-700">
        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 mx-auto max-w-7xl grid-lines border-r border-l border-slate-100"></div>
          <div className="absolute top-0 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 w-[80vw] h-[400px] bg-[radial-gradient(ellipse_at_center,_var(--accent-glow),_transparent_70%)] opacity-40 blur-3xl"></div>
        </div>

        {/* Navigation */}
        <div className="fixed top-6 start-0 end-0 z-50 flex justify-center px-6 animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0s_both]">
          <nav className="flex w-full max-w-5xl items-center justify-between rounded-full border border-slate-200 bg-white/80 p-2 ps-6 shadow-xl backdrop-blur-xl ring-1 ring-slate-900/5">
            <a href={localizedPath('/')} className="flex items-center gap-3 no-underline">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
                <iconify-icon icon="lucide:activity" width="18" height="18"></iconify-icon>
              </div>
              <span className="text-base font-bold tracking-tight text-slate-900">
                Saqr Skills
              </span>
            </a>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
              <a href="#assessment-types" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.assessments')}</a>
              <a href="#trainer-experience" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.forTrainers')}</a>
              <a href="#enterprise" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.enterprise')}</a>
              <a href="#platform" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.platform')}</a>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              <a href={localizedPath('/book-call')} className="group relative flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-slate-800 shadow-lg shadow-slate-900/20 no-underline">
                <span>{t('common:nav.getStarted')}</span>
                <iconify-icon icon="lucide:arrow-right" width="14" class="opacity-50 transition-transform group-hover:translate-x-0.5"></iconify-icon>
              </a>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl z-10 mx-auto pt-12 px-6 pb-32 relative">
          {/* Hero Section */}
          <div className="mx-auto mb-24 max-w-4xl text-center">
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.1s_both] mb-8 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-700 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
              </span>
              {t('hero.badge')}
            </div>
            <h1 className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] mb-6 text-6xl font-semibold leading-[0.95] tracking-tight text-slate-900 md:text-8xl">
              {t('hero.title')}
            </h1>
            <p className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] mx-auto mb-4 max-w-2xl text-2xl font-normal leading-relaxed text-slate-600">
              {t('hero.subtitle')}
              <span className="font-serif-accent italic text-slate-500"> {t('hero.subtitleAccent')}</span>
            </p>
            <p className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.35s_both] mx-auto mb-10 max-w-xl text-lg font-normal leading-relaxed text-slate-500">
              {t('hero.description')}
            </p>
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] flex flex-col items-center justify-center gap-6">
              <a
                href={localizedPath('/book-call')}
                className="h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30 flex items-center gap-2 no-underline">
                {t('common:cta.designFirstAssessment')}
                <iconify-icon icon="lucide:arrow-right" width="16"></iconify-icon>
              </a>
            </div>
          </div>

          {/* What is Saqr Skills */}
          <div className="mx-auto mt-24 mb-24 max-w-4xl animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.5s_both]">
            <div className="text-center mb-12">
              <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                {t('whatIs.label')}
              </span>
              <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                {t('whatIs.title')}
              </h2>
            </div>
            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
              {/* Translation strings with HTML come from our own JSON files, not user input */}
              <p dangerouslySetInnerHTML={{ __html: t('whatIs.p1') }} />
              <p>{t('whatIs.p2')}</p>
              <div className="mt-8 p-6 rounded-2xl border border-slate-200 bg-slate-50">
                <p className="text-slate-700 font-medium mb-2">{t('whatIs.focusTitle')}</p>
                <p className="text-slate-500">
                  <Trans
                    i18nKey="whatIs.focusDesc"
                    ns="terms"
                    components={{
                      link: <a href="https://saqrintelligence.com" className="text-sky-600 hover:text-sky-700 no-underline font-medium" />
                    }}
                  />
                </p>
              </div>
            </div>
          </div>

          {/* Assessment Types */}
          <section id="assessment-types" className="scroll-mt-32 mt-32">
            <div className="text-center mb-16 animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
              <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                {t('assessmentTypes.label')}
              </span>
              <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                {t('assessmentTypes.title')}
              </h2>
              <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
                {t('assessmentTypes.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Voice Assessments - Primary */}
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] relative overflow-hidden rounded-3xl border-2 border-sky-200 bg-gradient-to-b from-sky-50 to-white p-8 shadow-xl">
                <div className="absolute top-4 end-4">
                  <span className="text-[10px] uppercase tracking-widest text-sky-600 font-bold bg-sky-100 px-2 py-1 rounded-full">
                    {t('assessmentTypes.voice.badge')}
                  </span>
                </div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-lg shadow-sky-500/30">
                  <iconify-icon icon="lucide:mic" width="28"></iconify-icon>
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-3">{t('assessmentTypes.voice.title')}</h3>
                <p className="text-slate-600 mb-6">{t('assessmentTypes.voice.description')}</p>

                <div className="mb-6">
                  <p className="text-sm font-semibold text-slate-700 mb-3">{t('assessmentTypes.voice.bestFor')}</p>
                  <div className="flex flex-wrap gap-2">
                    {voiceBestForItems.map((item) => (
                      <span key={item} className="text-xs bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-sky-100">
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {t('assessmentTypes.voice.detail')}
                  </p>
                  <p className="text-slate-900 font-medium text-sm mt-3">
                    {t('assessmentTypes.voice.measure')}
                  </p>
                </div>
              </div>

              {/* Written Assessments */}
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-8 hover:border-sky-200 transition-colors">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-slate-200 text-sky-500 shadow-sm">
                  <iconify-icon icon="lucide:file-text" width="28"></iconify-icon>
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-3">{t('assessmentTypes.written.title')}</h3>
                <p className="text-slate-600 mb-6">{t('assessmentTypes.written.description')}</p>

                <div className="mb-6">
                  <p className="text-sm font-semibold text-slate-700 mb-3">{t('assessmentTypes.written.includes')}</p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <iconify-icon icon="lucide:check" class="text-sky-500 mt-0.5" width="16"></iconify-icon>
                      {/* Translation strings with HTML come from our own JSON files */}
                      <span dangerouslySetInnerHTML={{ __html: t('assessmentTypes.written.mcq') }} />
                    </li>
                    <li className="flex items-start gap-2">
                      <iconify-icon icon="lucide:check" class="text-sky-500 mt-0.5" width="16"></iconify-icon>
                      <span dangerouslySetInnerHTML={{ __html: t('assessmentTypes.written.sjt') }} />
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-semibold text-slate-700 mb-3">{t('assessmentTypes.written.bestFor')}</p>
                  <div className="flex flex-wrap gap-2">
                    {writtenBestForItems.map((item) => (
                      <span key={item} className="text-xs bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-200">
                  <p className="text-slate-500 text-sm">
                    {t('assessmentTypes.written.detail')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Trainer Experience */}
          <section id="trainer-experience" className="scroll-mt-32 mt-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
                <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                  {t('trainerExperience.label')}
                </span>
                <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                  {t('trainerExperience.title')}
                </h2>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                  {t('trainerExperience.description')}
                </p>
                <p className="mt-4 text-lg text-slate-700 font-medium">
                  {t('trainerExperience.blankPage')}
                </p>

                <div className="mt-8 space-y-4">
                  {trainerSteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <p className="text-slate-600 pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both]">
                <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex-shrink-0 text-sky-500">
                        <iconify-icon icon="lucide:sparkles" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">{t('trainerExperience.proposes.title')}</h4>
                        <p className="text-xs text-slate-500 mt-1">{t('trainerExperience.proposes.desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex-shrink-0 text-sky-500">
                        <iconify-icon icon="lucide:check-circle" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">{t('trainerExperience.approves.title')}</h4>
                        <p className="text-xs text-slate-500 mt-1">{t('trainerExperience.approves.desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                      <div className="flex-shrink-0 text-sky-600">
                        <iconify-icon icon="lucide:zap" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">{t('trainerExperience.noConfig.title')}</h4>
                        <p className="text-xs text-slate-500 mt-1">{t('trainerExperience.noConfig.desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Learner Experience */}
          <section className="mt-32">
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-12">
              <div className="text-center mb-12">
                <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                  {t('learnerExperience.label')}
                </span>
                <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                  {t('learnerExperience.title')}
                </h2>
                <p className="mt-4 text-lg text-slate-600">
                  {t('learnerExperience.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {learnerFeatures.map((item) => (
                  <div key={item.title} className="text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white border border-slate-200 text-sky-500">
                      <iconify-icon icon={item.icon} width="24"></iconify-icon>
                    </div>
                    <h4 className="font-semibold text-slate-900">{item.title}</h4>
                    <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <iconify-icon icon="lucide:mic" class="text-sky-500" width="20"></iconify-icon>
                    <h4 className="font-semibold text-slate-900">{t('learnerExperience.voice.title')}</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    {voiceItems.map((item, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <iconify-icon icon="lucide:file-text" class="text-sky-500" width="20"></iconify-icon>
                    <h4 className="font-semibold text-slate-900">{t('learnerExperience.written.title')}</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    {writtenItems.map((item, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="text-center text-slate-500 text-sm mt-8">
                {t('learnerExperience.note')}
              </p>
            </div>
          </section>

          {/* Scoring & Evidence */}
          <section className="mt-32">
            <div className="text-center mb-16 animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
              <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                {t('scoring.label')}
              </span>
              <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                {t('scoring.title')}
              </h2>
            </div>

            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {scoringIconFeatures.map((item) => (
                <div key={item.label} className="text-center p-6 rounded-2xl border border-slate-200 bg-white hover:border-sky-200 transition-colors">
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <iconify-icon icon={item.icon} width="20"></iconify-icon>
                  </div>
                  <p className="text-sm font-medium text-slate-700">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] max-w-3xl mx-auto">
              <div className="p-6 rounded-2xl border border-slate-200 bg-slate-50">
                <p className="text-slate-700 font-medium mb-4">{t('scoring.immutableTitle')}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {scoringImmutableIcons.map((item) => (
                    <div key={item.label} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200">
                      <iconify-icon icon={item.icon} class="text-sky-500" width="18"></iconify-icon>
                      <span className="text-sm text-slate-700">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Enterprise Ready */}
          <section id="enterprise" className="scroll-mt-32 mt-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
                <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                  {t('enterprise.label')}
                </span>
                <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                  {t('enterprise.title')}
                </h2>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                  {t('enterprise.description')}
                </p>

                <div className="mt-8 space-y-4">
                  {enterpriseFeatures.map((item) => (
                    <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <iconify-icon icon="lucide:check-circle-2" class="text-sky-500 mt-0.5" width="20"></iconify-icon>
                      <div>
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <p className="text-sm text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both]">
                <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <iconify-icon icon="lucide:arrow-right-circle" class="text-sky-500" width="24"></iconify-icon>
                    {/* Translation strings with HTML come from our own JSON files */}
                    <p className="text-slate-700" dangerouslySetInnerHTML={{ __html: t('enterprise.integration') }} />
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    {t('enterprise.integrationDesc')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Who It's For */}
          <section className="mt-32">
            <div className="text-center mb-16 animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
              <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                {t('whoFor.label')}
              </span>
              <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                {t('whoFor.title')}
              </h2>
            </div>

            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {whoForIcons.map((item) => (
                <div key={item.text} className="flex items-start gap-4 p-6 rounded-2xl border border-slate-200 bg-white hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50 transition-all">
                  <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <iconify-icon icon={item.icon} width="20"></iconify-icon>
                  </div>
                  <p className="text-slate-700 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Platform Integration */}
          <section id="platform" className="scroll-mt-32 mt-32">
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-12">
              <div className="text-center mb-12">
                <span className="text-[11px] uppercase tracking-widest text-sky-600 font-semibold">
                  {t('platform.label')}
                </span>
                <h2 className="mt-4 text-4xl font-semibold text-slate-900 tracking-tight">
                  {t('platform.title')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-lg shadow-sky-500/30">
                    <iconify-icon icon="lucide:activity" width="28"></iconify-icon>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('platform.skills.title')}</h3>
                  <p className="text-slate-600">{t('platform.skills.desc')}</p>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                    <iconify-icon icon="lucide:bar-chart-3" width="28"></iconify-icon>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('platform.intelligence.title')}</h3>
                  <p className="text-slate-600">{t('platform.intelligence.desc')}</p>
                </div>
              </div>

              <p className="text-center text-slate-700 font-medium mt-8">
                {t('platform.together')}
              </p>
            </div>
          </section>
        </main>

        {/* CTA Section */}
        <section className="relative border-t border-slate-200 bg-slate-900 py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.15),_transparent_60%)]"></div>
          <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
            <h2 className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] text-4xl font-semibold text-white tracking-tight">
              {t('cta.title')}
            </h2>
            <p className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] mt-4 text-lg text-slate-400 leading-relaxed">
              {t('cta.description')}<br />
              {t('cta.description2')}
            </p>
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] mt-8">
              <a
                href={localizedPath('/book-call')}
                className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-sky-400 shadow-lg shadow-sky-500/30 no-underline"
              >
                {t('common:cta.talkToOurTeam')}
                <iconify-icon icon="lucide:arrow-right" width="16"></iconify-icon>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-slate-50 pb-12 pt-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-16 flex flex-col justify-between gap-12 md:flex-row">
              <div className="max-w-xs">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-900 text-white">
                    <iconify-icon icon="lucide:activity" width="14" height="14"></iconify-icon>
                  </div>
                  <span className="text-lg font-bold tracking-tight text-slate-900">
                    Saqr Skills
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-slate-500">
                  {t('common:footer.tagline')}
                </p>
              </div>
              <div className="flex gap-16 text-sm text-slate-500">
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.platform')}</span>
                  <a href="#assessment-types" className="no-underline text-slate-500 hover:text-sky-600">{t('common:nav.assessments')}</a>
                  <a href="#trainer-experience" className="no-underline text-slate-500 hover:text-sky-600">{t('common:nav.forTrainers')}</a>
                  <a href="#enterprise" className="no-underline text-slate-500 hover:text-sky-600">{t('common:nav.enterprise')}</a>
                </div>
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.company')}</span>
                  <a href="https://saqracademy.com" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.saqrAcademy')}</a>
                  <a href="https://saqrintelligence.com" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.saqrIntelligence')}</a>
                </div>
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.connect')}</span>
                  <a href="https://www.linkedin.com/company/saqr-academy" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.linkedin')}</a>
                  <a href="mailto:hello@saqracademy.com" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.email')}</a>
                  <a href={localizedPath('/book-call')} className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.contact')}</a>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-8">
              <p className="text-xs text-slate-500">
                &copy; {t('common:footer.copyright')}
              </p>
              <div className="flex gap-2 items-center">
                <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                <span className="text-xs text-slate-500 font-medium">
                  {t('common:footer.allSystemsOperational')}
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
